import { FC, useState, useEffect } from 'react';
import { KatFileUpload, KatButton } from '@amzn/katal-react';
import CropIcon from '@mui/icons-material/Crop';
import { MaterialRadioButtonGroup } from 'src/components/general/MaterialRadioButtonGroup';
import { ConfirmationDialog } from 'src/components/general/ConfirmationDialog';
import { CropModal, CropOrientation } from 'src/components/general/CropModal/CropModal';
import { StatusIndicator } from 'src/components/general/StatusIndicator';
import { useAssetInput } from 'src/hooks/assetInput/useAssetInput';
import { InputType, AssetStatus } from 'src/types';
import { UrlInput } from './UrlInput';
import { SearchImageInput } from 'src/components/pages/campaignAutomation/input/assets/SearchImageInput';
import { AssetInputProps, INPUT_OPTIONS } from '.';
import styles from './AssetInput.module.css';
import { getFileUploadState } from 'src/utils/assetInput/assetInputUtils';
import { AssetConstants, StateConstants } from 'src/constants';
import { StyledDropdown } from 'src/components/general/StyledDropdown';
import { getVariantsFromConfig } from 'src/config/templateVariantConfiguration';
import { ProvisionedConstants, STAGE } from 'src/constants';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { VideoDurationInput } from './VideoDurationInput';
import {
  AssetType,
  DeviceFamily,
  Placement,
  TransformationVariant,
} from '@amzn/fire-tv-campaign-automation-service';
import { LogoTextInput } from './LogoTextInput';
import { useAssetOperationsInProgress } from 'src/hooks/useAssetOperationsInProgress';
import { useSelection } from 'src/contexts/SelectionContext';
import { OverlaySection } from './OverlaySection';

const ENABLED_STATUSES_FOR_VIDEO_DURATION = [
  AssetStatus.COMPLETED,
  AssetStatus.PENDING_TRANSFORMATION,
  AssetStatus.BASIC_VALIDATION_SUCCESS,
];

const ENABLED_STATUSES_FOR_CROP_MODAL = [
  AssetStatus.BASIC_VALIDATION_SUCCESS,
  AssetStatus.COMPLETED,
];

const SEARCH_ENABLED_STATUSES: readonly AssetStatus[] = [
  AssetStatus.FAILED,
  AssetStatus.COMPLETED,
  AssetStatus.PENDING_INPUT,
  AssetStatus.BASIC_VALIDATION_FAILED,
];

/**
 * Component for handling asset input through various methods (URL, file upload, or logo text)
 * @param {AssetInputProps} props - The component props
 * @returns {JSX.Element} Asset input component with radio buttons for input type selection
 */
export const AssetInput: FC<AssetInputProps> = ({
  label,
  assetInputOptions,
  assetInputHandler,
  acceptedFileTypes,
  maxFileSizeInBytes,
  assetTransformationHandler,
  error,
  status,
  assetType,
  assetDetails,
  assetValidationHandler,
  setState,
  disabled = false,
}) => {
  const {
    selectedInputType,
    url,
    showConfirmDialog,
    radioGroupName,
    isValidUrlInput,
    selectedImageTitle,
    deviceFamily,
    placement,
    handleUrlChange,
    handleUrlFetch,
    handleCatalogSelection,
    handleUrlRemoval,
    handleFileAttached,
    handleFileRemoved,
    handleFileReplaced,
    handleFileAttachedError,
    handleRadioChange,
    handleConfirmTypeChange,
    handleCancelTypeChange,
    handleTransformationVariantChange,
    handleClearSelectedImage,
    getConfirmationMessage,
  } = useAssetInput({
    label,
    assetType,
    assetInputHandler,
    status,
    setState,
    assetDetails,
    assetValidationHandler,
    assetTransformationHandler,
  });

  const { assetStates } = useAssetContext();
  const { marketplace } = useSelection();
  const fileUploadStatus = getFileUploadState(status, error);
  const radioButtonInputOptions = Object.keys(assetInputOptions)
    .map((option) => INPUT_OPTIONS[option as InputType])
    .filter((option): option is NonNullable<typeof option> => option !== undefined);

  const isAssetOperationInProgress = useAssetOperationsInProgress(assetType);

  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [imageKey, setImageKey] = useState<string | undefined>();

  const transformationVariant = assetStates[assetType]?.transformationVariant;

  const getCropOrientation = (): CropOrientation | undefined => {
    if (assetType !== AssetType.BACKGROUND_IMAGE) return undefined;
    if (transformationVariant === TransformationVariant.FULL_BLEED) return 'vertical';
    if (
      transformationVariant === TransformationVariant.DOMINANT_COLOR_FADED_OVERLAY &&
      placement === Placement.SCREEN_SAVER
    )
      return 'horizontal';
    return undefined;
  };

  const cropOrientation = getCropOrientation();

  const showCropControls = cropOrientation !== undefined;

  // Manage blob URL lifecycle
  useEffect(() => {
    if (assetDetails.file) {
      const url = URL.createObjectURL(assetDetails.file);
      setImageKey(url);
      return () => URL.revokeObjectURL(url);
    }
    setImageKey(undefined);
  }, [assetDetails.file]);

  useEffect(() => {
    if (showCropControls && status === AssetStatus.BASIC_VALIDATION_SUCCESS) {
      setIsCropModalOpen(true);
    }
  }, [showCropControls, status]);

  const renderInput = () => {
    switch (selectedInputType) {
      case InputType.URL:
        return (
          <UrlInput
            url={url}
            status={status}
            onChange={handleUrlChange}
            onFetch={handleUrlFetch}
            onClear={handleUrlRemoval}
            isValidUrl={isValidUrlInput}
            isFetching={status === AssetStatus.PENDING_UPLOAD}
            disabled={disabled}
          />
        );
      case InputType.UPLOAD:
        const isUploadDisabled = disabled || isAssetOperationInProgress;
        return (
          <div
            style={{
              pointerEvents: isUploadDisabled ? 'none' : 'auto',
              opacity: isUploadDisabled ? 0.5 : 1,
            }}
          >
            <KatFileUpload
              accept={acceptedFileTypes}
              maxSize={maxFileSizeInBytes}
              onFilesAttached={(event) => handleFileAttached(event)}
              onFilesRemoved={handleFileRemoved}
              onFilesReplaced={(event) => handleFileReplaced(event)}
              onFilesAttachedError={handleFileAttachedError}
              hideClearAll
              state={fileUploadStatus}
              disabled={isUploadDisabled}
              data-testid="file-upload"
            >
              <div slot="error">{error}</div>
            </KatFileUpload>
          </div>
        );
      case InputType.SEARCH:
        return (
          <SearchImageInput
            label="Search by Title or Keyword"
            placeholder="Enter movie or content name"
            onImageSelect={(imageUrl: string, imageTitle?: string) =>
              handleCatalogSelection(imageUrl, imageTitle)
            }
            selectedImageUrl={url}
            selectedImageTitle={selectedImageTitle}
            onClearSelection={handleClearSelectedImage}
            maxLength={AssetConstants.SEARCH_QUERY_MAX_LENGTH}
            disabled={disabled || !SEARCH_ENABLED_STATUSES.includes(status)}
          />
        );
      case InputType.LOGO_TEXT:
        return (
          <LogoTextInput
            logoTextState={assetStates[assetType]}
            updateAssetState={setState}
            disabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.container} data-testid="asset-input">
      {label && <label className={styles.assetTitle}>{label}</label>}

      <MaterialRadioButtonGroup
        name={radioGroupName}
        options={radioButtonInputOptions}
        value={selectedInputType}
        onChange={handleRadioChange}
        data-testid="input-type-selector"
        disabled={disabled}
      />

      {assetInputOptions[selectedInputType as InputType] && (
        <StyledDropdown
          options={
            getVariantsFromConfig(ProvisionedConstants.STAGE as STAGE, deviceFamily, placement)?.[
              assetType
            ]
          }
          value={assetStates[assetType]?.transformationVariant}
          onChange={handleTransformationVariantChange}
          label={'Transformation Template'}
          data-testid={`${assetType}-transformation-variant-selector`}
          disabled={disabled}
        />
      )}

      {renderInput()}

      {showCropControls && assetDetails.file && (
        <div className={styles.editCropContainer}>
          <KatButton
            variant="secondary"
            size="small"
            onClick={() => setIsCropModalOpen(true)}
            disabled={!ENABLED_STATUSES_FOR_CROP_MODAL.includes(status)}
            data-testid="edit-crop-button"
          >
            <CropIcon fontSize="small" className={styles.cropIcon} />{' '}
            <span className={styles.cropText}>Crop Image</span>
          </KatButton>
        </div>
      )}

      {assetType === AssetType.VIDEO_TRAILER &&
        assetStates[assetType]?.transformationVariant !==
          TransformationVariant.NO_TRANSFORMATION && (
          <VideoDurationInput
            videoState={assetStates[assetType]}
            updateAssetState={setState}
            videoDuration={assetStates[assetType]?.videoDuration}
            disabled={!ENABLED_STATUSES_FOR_VIDEO_DURATION.includes(status)}
            data-testid="video-duration-input"
          />
        )}

      {showCropControls && ENABLED_STATUSES_FOR_CROP_MODAL.includes(status) && (
        <CropModal
          isVisible={isCropModalOpen}
          onClose={() => setIsCropModalOpen(false)}
          imageState={assetStates[assetType]}
          updateAssetState={setState}
          imageKey={imageKey}
          disabled={!ENABLED_STATUSES_FOR_CROP_MODAL.includes(status)}
          orientation={cropOrientation}
          data-testid="crop-modal"
        />
      )}

      {assetType === AssetType.BACKGROUND_IMAGE && deviceFamily === DeviceFamily.FIRE_TV && (
        <OverlaySection
          assetState={assetStates[assetType]}
          marketplace={marketplace}
          enabled={status === AssetStatus.COMPLETED && !!assetStates[assetType]?.key}
          setState={setState}
        />
      )}

      <StatusIndicator
        // for Input type of file upload input error is thrown in KatFileUpload component
        error={selectedInputType !== InputType.UPLOAD ? error : StateConstants.EMPTY_STRING}
        isLoading={isAssetOperationInProgress}
        isSuccess={status === AssetStatus.COMPLETED}
        data-testid="asset-input-status"
      />

      <ConfirmationDialog
        isVisible={showConfirmDialog}
        title="Change Input"
        message={getConfirmationMessage()}
        onConfirm={handleConfirmTypeChange}
        onCancel={handleCancelTypeChange}
        data-testid="asset-input-confirm-dialog"
      />
    </div>
  );
};
